import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Award,
  Bot,
  ChevronRight,
  Download,
  FileText,
  MessageSquare,
  Video,
} from "lucide-react";
import { API } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { PublicSiteHeader } from "@/components/PublicSiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CourseCard } from "@/components/CourseCard";

export const LandingPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const { data } = await API.get("/courses", { params: { limit: 6 } });
      setCourses(data);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <PublicSiteHeader />

      <main id="main-content">
      <section className="mesh-hero py-20 sm:py-24 lg:py-32 px-4 sm:px-6 md:px-12 lg:px-24 relative">
        <div
          className="glow-orb w-[420px] h-[420px] bg-[#4d6bff]/40 -top-32 -left-24"
          aria-hidden="true"
        />
        <div
          className="glow-orb w-[360px] h-[360px] bg-[#1b0f5c]/60 top-1/3 -right-16"
          style={{ animationDelay: "-6s" }}
          aria-hidden="true"
        />
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
          <div>
            <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#a9b8ff] mb-6">
              <span className="w-6 rule-gradient" style={{ background: "linear-gradient(90deg, #4d6bff, transparent)" }} />
              {t("landing.overline")}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight font-medium text-white mb-6 text-balance">
              {t("landing.headline")
                .split(" ")
                .map((word, i, arr) =>
                  i === arr.length - 1 ? (
                    <span key={i} className="text-gradient-klein">{word}</span>
                  ) : (
                    `${word} `
                  )
                )}
            </h1>
            <p className="text-base sm:text-lg leading-relaxed text-slate-300 mb-10 max-w-lg">
              {t("landing.subheadline")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate(user ? "/dashboard" : "/register")}
                className="glow-on-hover inline-flex items-center justify-center gap-2 rounded-sm bg-[#002FA7] text-white px-8 py-6 text-lg font-medium border border-[#4d6bff]/40"
                data-testid="hero-cta-btn"
              >
                {t("landing.startLearning")}
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/courses")}
                className="glass-panel rounded-sm px-8 py-6 text-lg text-white hover:bg-white/10 hover:text-white"
                data-testid="browse-courses-btn"
              >
                {t("landing.browseCourses")}
              </Button>
            </div>
          </div>
          <div className="hidden lg:block relative" aria-hidden="true">
            <div className="glass-panel rounded-sm shadow-2xl overflow-hidden p-1">
              <svg viewBox="0 0 480 332" className="w-full h-auto block" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="heroCardGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#1b2a6b" />
                    <stop offset="100%" stopColor="#0a0b16" />
                  </linearGradient>
                </defs>
                <rect width="480" height="332" fill="url(#heroCardGrad)" />
                <line x1="0" y1="83" x2="480" y2="83" stroke="#7d94ff" strokeOpacity="0.1" />
                <line x1="0" y1="166" x2="480" y2="166" stroke="#7d94ff" strokeOpacity="0.1" />
                <line x1="0" y1="249" x2="480" y2="249" stroke="#7d94ff" strokeOpacity="0.1" />
                <line x1="120" y1="0" x2="120" y2="332" stroke="#7d94ff" strokeOpacity="0.1" />
                <line x1="360" y1="0" x2="360" y2="332" stroke="#7d94ff" strokeOpacity="0.1" />

                {/* Course video card */}
                <rect x="56" y="52" width="272" height="176" fill="#050614" />
                <rect x="56" y="52" width="272" height="176" fill="#4d6bff" fillOpacity="0.14" />
                <circle cx="192" cy="140" r="30" fill="#FFFFFF" />
                <path d="M182 124 L182 156 L210 140 Z" fill="#002FA7" />
                <rect x="56" y="238" width="180" height="10" fill="#FFFFFF" fillOpacity="0.9" />
                <rect x="56" y="256" width="120" height="8" fill="#FFFFFF" fillOpacity="0.4" />

                {/* Certificate card */}
                <rect x="284" y="128" width="150" height="150" fill="#0f1230" stroke="#7d94ff" strokeOpacity="0.35" />
                <circle cx="359" cy="172" r="22" fill="#4d6bff" />
                <path d="M349 172 L356 180 L371 163" stroke="#FFFFFF" strokeWidth="4" fill="none" strokeLinecap="square" />
                <rect x="309" y="208" width="100" height="7" fill="#FFFFFF" fillOpacity="0.9" />
                <rect x="309" y="222" width="70" height="6" fill="#FFFFFF" fillOpacity="0.4" />
                <rect x="309" y="238" width="100" height="1" fill="#7d94ff" fillOpacity="0.4" />
                <rect x="309" y="248" width="55" height="6" fill="#FFFFFF" fillOpacity="0.3" />

                {/* Accent dots */}
                <circle cx="70" cy="292" r="4" fill="#7d94ff" />
                <circle cx="90" cy="292" r="4" fill="#7d94ff" fillOpacity="0.5" />
                <circle cx="110" cy="292" r="4" fill="#7d94ff" fillOpacity="0.25" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4 sm:px-6 md:px-12 lg:px-24 bg-white relative overflow-hidden">
        <div className="container mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4 text-center">
            {t("landing.featuresOverline")}
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#0A0B10] mb-12 text-center">
            {t("landing.featuresTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Video,
                title: t("landing.features.videoCourses"),
                desc: t("landing.features.videoCoursesDesc"),
              },
              {
                icon: FileText,
                title: t("landing.features.quizzes"),
                desc: t("landing.features.quizzesDesc"),
              },
              {
                icon: Award,
                title: t("landing.features.certificates"),
                desc: t("landing.features.certificatesDesc"),
              },
              {
                icon: MessageSquare,
                title: t("landing.features.forums"),
                desc: t("landing.features.forumsDesc"),
              },
              {
                icon: Bot,
                title: t("landing.features.aiAssistant"),
                desc: t("landing.features.aiAssistantDesc"),
              },
              {
                icon: Download,
                title: t("landing.features.materials"),
                desc: t("landing.features.materialsDesc"),
              },
            ].map((f, i) => (
              <Card
                key={i}
                className="card-swiss card-elevate group"
                data-testid={`feature-card-${i}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 grid place-items-center bg-[#002FA7]/[0.07] mb-5 transition-colors duration-300 group-hover:bg-[#002FA7]">
                    <f.icon className="w-6 h-6 text-[#002FA7] transition-colors duration-300 group-hover:text-white" />
                  </div>
                  <h3 className="text-xl text-[#0A0B10] mb-2">{f.title}</h3>
                  <p className="text-slate-600">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {courses.length > 0 && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 md:px-12 lg:px-24 bg-[#F4F5F7]">
          <div className="container mx-auto">
            <div className="flex justify-between items-center mb-12">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-2">
                  {t("landing.popularCourses")}
                </p>
                <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-[#0A0B10]">
                  {t("landing.startJourney")}
                </h2>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/courses")}
                className="border-slate-200 rounded-sm"
                data-testid="view-all-courses-btn"
              >
                {t("landing.viewAll")} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          </div>
        </section>
      )}
      </main>

      <SiteFooter />
    </div>
  );
};
